import { useCallback, useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	useSidebar,
} from "@ncoder/ui";
import { ResizeGrip } from "@/components/atoms/ResizeGrip";
import { useTestingLayout } from "@/hooks/testing/useTestingLayout";
import { useTestingShell } from "@/hooks/testing/useTestingShell";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useOverlayFocus } from "@/hooks/ui/useOverlayFocus";
import { TestingChatPanel } from "./TestingChatPanel";
import { TestingInspector } from "./TestingInspector";
import { TestingPlanList, TestingSessionRail } from "./TestingSessionList";
import { TestingWorkspaceHeader } from "./TestingWorkspaceHeader";
import "@/pages/testing/styles/testing-tokens.css";

export function TestingLayout() {
	const vm = useTestingLayout();
	const shell = useTestingShell();

	const {
		sessions,
		activeSession,
		activeSessionPlans,
		plansQuery,
		selectedPlan,
		selectedPlanId,
		runningHere,
		healingHere,
		runningElsewhere,
		planningActive,
		runStartedAtMs,
		healStage,
		primaryAction,
		canSelfHealRun,
		healDisabled,
		showCredentialError,
		setShowCredentialError,
		getAuthUrlMutation,
		selectSession,
		newPlanChat,
		editPlanInChat,
		applyRecommendationInChat,
		onStop,
		onHeal,
		handleRename,
		chat,
	} = vm;

	const anyLive = sessions.some((s) => s.live);

	// ── App nav auto-collapse (same contract as MultiAgentRoute) ───────────────
	// Collapse the app sidebar once when the user first engages with Testing so the workspace gets
	// maximum width. A ref guard means re-expanding it manually mid-session is respected; the flag
	// resets when the session ends so the next one can trigger it again.
	const { setOpen: setSidebarOpen } = useSidebar();
	const hasActiveSession = Boolean(selectedPlanId) || chat.messages.length > 0;
	const sidebarAutoCollapsed = useRef(false);
	useEffect(() => {
		if (!hasActiveSession) {
			sidebarAutoCollapsed.current = false;
			return;
		}
		if (!sidebarAutoCollapsed.current) {
			sidebarAutoCollapsed.current = true;
			setSidebarOpen(false);
		}
	}, [hasActiveSession, setSidebarOpen]);

	// ── Keyboard ──────────────────────────────────────────────────────────────
	// Panel toggles mirror the conventions users already carry in from VS Code / Linear.
	useKeyboardShortcuts({
		shortcuts: [
			{
				combo: [
					{ key: "b", modifiers: ["meta"], preventDefault: true },
					{ key: "b", modifiers: ["ctrl"], preventDefault: true },
				],
				handler: shell.sessions.toggle,
				description: "Toggle the session list",
			},
			{
				combo: [
					{ key: "j", modifiers: ["meta"], preventDefault: true },
					{ key: "j", modifiers: ["ctrl"], preventDefault: true },
				],
				handler: shell.inspector.toggle,
				description: "Toggle run details",
			},
			{
				combo: [
					{ key: "n", modifiers: ["meta", "shift"], preventDefault: true },
					{ key: "n", modifiers: ["ctrl", "shift"], preventDefault: true },
				],
				handler: newPlanChat,
				description: "Start a new test",
			},
		],
	});

	// Stable handlers: the session list is memoized, and inline arrows here would defeat that on every
	// streamed activity event.
	const { closeDrawers } = shell;
	const handleSelectPlan = useCallback(
		(plan: Parameters<typeof selectSession>[0]) => {
			selectSession(plan);
			// On a narrow viewport the list floats over the chat; picking a plan should reveal what was
			// picked (and let the scroll-to-plan land) rather than leaving the drawer covering it.
			closeDrawers();
		},
		[selectSession, closeDrawers],
	);
	const handleNewSession = useCallback(() => {
		newPlanChat();
		closeDrawers();
	}, [newPlanChat, closeDrawers]);

	const sessionList = (
		<TestingPlanList
			plans={activeSessionPlans}
			sessionTitle={activeSession?.title ?? null}
			activePlanId={selectedPlanId}
			draft={activeSession?.kind === "draft"}
			loading={plansQuery.isLoading || plansQuery.isFetching}
			onSelectPlan={handleSelectPlan}
			onNew={handleNewSession}
			onCollapse={shell.sessions.close}
			asDrawer={shell.sessions.isDrawer}
			onUseInChat={applyRecommendationInChat}
		/>
	);

	const inspector = (
		<TestingInspector
			vm={vm}
			width={shell.inspector.isDrawer ? undefined : shell.inspector.drag.width}
			onCollapse={shell.inspector.close}
			asDrawer={shell.inspector.isDrawer}
		/>
	);

	const anyDrawerOpen = shell.sessions.isDrawer || shell.inspector.isDrawer;
	// Keyboard users get focus moved into an opened drawer, contained while it is open, and returned
	// to the control that opened it on close.
	const findSessionsReveal = useCallback(
		() => document.querySelector<HTMLElement>('[data-tst-reveal="sessions"]'),
		[],
	);
	const findInspectorReveal = useCallback(
		() => document.querySelector<HTMLElement>('[data-tst-reveal="inspector"]'),
		[],
	);
	const sessionsDrawerRef = useOverlayFocus(shell.sessions.isDrawer, findSessionsReveal);
	const inspectorDrawerRef = useOverlayFocus(shell.inspector.isDrawer, findInspectorReveal);

	return (
		<div className="tst-route flex h-full min-h-0 overflow-hidden">
			{/* ── Session list: inline column, rail, or overlay drawer ── */}
			{shell.sessions.mode === "full" && shell.sessions.isInline && (
				<>
					<div className="shrink-0" style={{ width: shell.sessions.drag.width }}>
						{sessionList}
					</div>
					<ResizeGrip drag={shell.sessions.drag} title="Resize the session list" />
				</>
			)}
			{shell.sessions.mode === "rail" && (
				<TestingSessionRail
					live={anyLive}
					onExpand={shell.sessions.open}
					onNew={handleNewSession}
				/>
			)}

			{/* ── Workspace ── */}
			<div className="tst-workspace relative flex min-w-0 flex-1 flex-col overflow-hidden">
				<TestingWorkspaceHeader
					selectedPlan={selectedPlan ?? null}
					draftTitle={activeSession?.title ?? null}
					loading={plansQuery.isLoading}
					runningHere={runningHere}
					healingHere={healingHere}
					runningElsewhere={runningElsewhere}
					planningActive={planningActive}
					runStartedAtMs={runStartedAtMs}
					healStage={healStage}
					primaryAction={primaryAction}
					onRun={() => selectedPlanId && void vm.runPlanById(selectedPlanId)}
					onStop={onStop}
					onEdit={() => selectedPlan && editPlanInChat(selectedPlan)}
					onHeal={onHeal}
					onRename={handleRename}
					canSelfHeal={canSelfHealRun}
					healDisabled={Boolean(healDisabled)}
					onRevealSessions={shell.sessions.mode === "hidden" ? shell.sessions.open : undefined}
					onRevealInspector={shell.inspector.mode === "hidden" ? shell.inspector.open : undefined}
				/>

				<div className="relative flex min-h-0 flex-1 overflow-hidden">
					<TestingChatPanel vm={vm} />

					{shell.inspector.mode === "full" && shell.inspector.isInline && (
						<>
							<ResizeGrip drag={shell.inspector.drag} title="Resize the run details panel" />
							{inspector}
						</>
					)}
					{shell.inspector.mode === "rail" && (
						<InspectorRail busy={runningHere || healingHere} onExpand={shell.inspector.open} />
					)}

					{/* Overlay drawers. Below `lg` a panel floats over the workspace instead of taking width
					    from it — the mechanism that keeps the chat readable at 1024px and below, where three
					    inline columns previously squeezed it to ~150px. */}
					{/* Scrim. A <button> here put a full-panel tab stop AHEAD of the drawer's own content;
					    it is decorative, so it is hidden from assistive tech and Escape (owned by the
					    shell) plus the drawer's close control provide the keyboard path. */}
					{anyDrawerOpen && (
						<div
							aria-hidden="true"
							onClick={shell.closeDrawers}
							className="absolute inset-0 z-20 cursor-default bg-background/40 backdrop-blur-[1px]"
						/>
					)}
					{shell.sessions.isDrawer && (
						<div
							ref={sessionsDrawerRef}
							className="tst-drawer"
							data-side="left"
							data-open="true"
							role="group"
							aria-label="Sessions"
						>
							{sessionList}
						</div>
					)}
					{shell.inspector.isDrawer && (
						<div
							ref={inspectorDrawerRef}
							className="tst-drawer"
							data-side="right"
							data-open="true"
							role="group"
							aria-label="Run details"
						>
							{inspector}
						</div>
					)}
				</div>
			</div>

			<CredentialErrorDialog
				open={showCredentialError}
				onClose={() => setShowCredentialError(false)}
				reconnecting={getAuthUrlMutation.isPending}
				onReconnect={() =>
					getAuthUrlMutation.mutate("sandbox", {
						onSuccess: (data) => {
							const url = new URL(data.authorizationUrl.url);
							url.searchParams.append("prompt", "login");
							window.location.href = url.toString();
						},
					})
				}
			/>
		</div>
	);
}

/** Collapsed inspector: 44px of "there is a live view behind this", with a pulse while a run streams. */
function InspectorRail({ busy, onExpand }: { busy: boolean; onExpand: () => void }) {
	return (
		<div className="tst-panel flex h-full w-11 shrink-0 flex-col items-center gap-1 border-l py-1.5">
			<button
				type="button"
				onClick={onExpand}
				className="tst-interactive relative rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
				title="Show run details (⌘J)"
				aria-label="Show run details"
			>
				<span className="sr-only">Show run details</span>
				<svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
					<rect x="3" y="4" width="18" height="16" rx="2" />
					<path d="M15 4v16" />
				</svg>
				{busy && (
					<span
						data-tst-decorative-motion
						className="absolute right-0.5 top-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-primary"
						aria-hidden
					/>
				)}
			</button>
		</div>
	);
}

/**
 * Salesforce session expiry. Uses the shared Dialog rather than the hand-rolled `fixed inset-0`
 * overlay this used to be — that version had no Escape handling, no focus trap and no scroll lock,
 * in a module that already uses the real Dialog for Suites and Coverage.
 */
function CredentialErrorDialog({
	open,
	onClose,
	reconnecting,
	onReconnect,
}: {
	open: boolean;
	onClose: () => void;
	reconnecting: boolean;
	onReconnect: () => void;
}) {
	return (
		<Dialog open={open} onOpenChange={(next) => !next && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-sm">
						<AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
						Salesforce connection expired
					</DialogTitle>
					<DialogDescription className="text-xs leading-relaxed">
						Your Salesforce session has expired or is no longer valid. Reconnect your org to continue
						running tests.
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="ghost" size="sm" onClick={onClose}>
						Dismiss
					</Button>
					<Button size="sm" disabled={reconnecting} onClick={onReconnect}>
						{reconnecting ? "Redirecting…" : "Reconnect Salesforce"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
